import CreateButton from "../components/CreateButton"
import { useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-pencil.svg"
import { ReactComponent as EmptyIllustration } from "../assets/images/illustrations/il-empty.svg"
import { ButtonStatus, CourseType } from "../common/constants"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import Button from "../components/Button"
import Dialog from "../components/dialogs/Dialog"
import EmptyContent from "../components/EmptyContent"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Pagination from "../components/Pagination"
import Select from "../components/Select"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Uploader from "../components/Uploader"
import Card from "../components/cards/Card"
import MaterialButton from "../components/MaterialButton"
import styles from "./Materials.module.css"
import AlertDialog from "../components/dialogs/AlertDialog"

const getMaterialFileType = (material) => {
  const source = material?.name || material?.url || ""
  const extension = source.includes(".") ? source.split(".").pop().toLowerCase() : ""

  if (!extension) return "-"

  const groupedTypes = {
    Immagine: ["jpg", "jpeg", "png", "svg", "webp", "gif", "avif", "apng", "heic"],
    Video: ["mp4", "m4p", "mov", "flv", "mkv", "avi", "wmv", "mpg", "mpeg", "m2v"],
    PDF: ["pdf"],
    Word: ["doc", "docx"],
    Excel: ["xls", "xlsx"],
    PowerPoint: ["ppt", "pptx"],
    Audio: ["mp3", "wav", "aac", "ogg", "m4a"],
    Archivio: ["zip", "rar", "7z", "tar", "gz"],
  }

  for (const [label, extensions] of Object.entries(groupedTypes)) {
    if (extensions.includes(extension)) {
      return label
    }
  }

  return extension.toUpperCase()
}

const normalizeSearchText = (value = "") => `${value}`.toLowerCase().trim().replaceAll(" ", "")

const filterSelectOptions = (options = [], query = "") => {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return options

  return options.filter((item) => normalizeSearchText(item?.name ?? item?.label).includes(normalizedQuery))
}

const Materials = () => {

  const { t } = useTranslation()
  const { width } = useWindowDimensions()

  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingPage, setLoadingPage] = useState(true)
  const [page, setPage] = useState(0)
  const per = 25
  const [count, setCount] = useState(null)
  const [statistics, setStatistics] = useState(null)
  const [showPagination, setShowPagination] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const [openDialog, setOpenDialog] = useState(false)
  const [courses, setCourses] = useState([])
  const [filterEditions, setFilterEditions] = useState([])
  const [editions, setEditions] = useState([])
  const [modules, setModules] = useState([])
  const [lessons, setLessons] = useState([])
  const [course, setCourse] = useState(null)
  const [edition, setEdition] = useState(null)
  const [module, setModule] = useState(null)
  const [lesson, setLesson] = useState(null)
  const [files, setFiles] = useState([])
  const [currentMaterial, setCurrentMaterial] = useState(null)
  const [waitingAdd, setWaitingAdd] = useState(false)
  const [formError, setFormError] = useState("")
  const [waitingEdit, setWaitingEdit] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [materialToDelete, setMaterialToDelete] = useState(null)

  const [filters, setFilters] = useState({
    courseId: null,
    editionId: null,
    moduleId: null,
    lessonId: null,
  })

  const [filterCourseSearch, setFilterCourseSearch] = useState("")
  const [filterEditionSearch, setFilterEditionSearch] = useState("")
  const [filterModuleSearch, setFilterModuleSearch] = useState("")
  const [filterLessonSearch, setFilterLessonSearch] = useState("")

  const [dialogCourseSearch, setDialogCourseSearch] = useState("")
  const [dialogEditionSearch, setDialogEditionSearch] = useState("")
  const [dialogModuleSearch, setDialogModuleSearch] = useState("")
  const [dialogLessonSearch, setDialogLessonSearch] = useState("")

  const [uploaderReset, setUploaderReset] = useState(0)

  useEffect(() => {
    getCourses()
  }, [])

  useEffect(() => {
    getMaterials();
  }, [page, per, filters, searchQuery]);

  useEffect(() => {
    setPage(0)
  }, [filters, searchQuery])

  useEffect(() => {
    let ignore = false

    const getFilterEditions = async () => {
      if (!filters.courseId) {
        setFilterEditions([])
        return
      }

      setFilterEditions([])

      try {
        const courseEditions = await api.get(`/admin/courses/${filters.courseId}/editions`)
        if (!ignore) {
          setFilterEditions(courseEditions)
        }
      } catch (e) {
        console.error(e)
      }
    }

    getFilterEditions()

    return () => {
      ignore = true
    }
  }, [filters.courseId])


  useEffect(() => {
    if (!openDialog) {
      onDialogClose()
    }
  }, [openDialog])

  useEffect(() => {
    if (course) {
      getEditions()
      if (currentMaterial && course.id !== currentMaterial.course?.id) {
        setCurrentMaterial(c => ({ ...c, course, edition: null, module: null, lesson: null }))
        setEditions([])
        setModules([])
        setLessons([])
        setEdition(null)
        setModule(null)
        setLesson(null)
      }
    } else {
      setEditions([])
      setModules([])
      setLessons([])
    }
  }, [course])

  useEffect(() => {
    if (edition) {
      setModules((edition.modules ?? []).map(m => ({ ...m, label: m.name })))
    } else {
      setModules([])
      setLessons([])
    }
  }, [edition])

  useEffect(() => {
    if (module) {
      setLessons((module.lessons ?? []).map(l => ({ ...l, label: l.name })))
    } else {
      setLessons([])
    }
  }, [module])

  useEffect(() => {
    if (currentMaterial) {
      const c = courses.find(cc => cc.id === currentMaterial.course.id)
      setCourse(c)
    }

  }, [currentMaterial])

  useEffect(() => {
    if (editions.length > 0 && currentMaterial && !edition) {
      const e = editions.find(ee => ee.id === currentMaterial.edition?.id)
      setEdition(e)
      setOpenDialog(true)
    }
  }, [editions])

  useEffect(() => {
    if (modules.length > 0 && currentMaterial && !module) {
      setModule(modules.find(m => m.id === currentMaterial.module?.id))
    }
  }, [modules])

  useEffect(() => {
    if (lessons.length > 0 && currentMaterial?.lesson && !lesson) {
      setLesson(lessons.find(l => l.id === currentMaterial.lesson?.id))
    }
  }, [lessons])

  const getMaterials = async () => {
    setLoading(true);
    try {

      const params = new URLSearchParams();

      params.append("page", page);
      params.append("per", per);

      if (filters.courseId) {
        params.append("courses", String(filters.courseId));
      }
      if (filters.editionId) {
        params.append("editions", String(filters.editionId));
      }
      if (filters.moduleId) {
        params.append("modules", String(filters.moduleId))
      }
      if (filters.lessonId) {
        params.append("lessons", String(filters.lessonId))
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("q", searchQuery.trim());
      }

      const data = await api.get(`/admin/materials?${params.toString()}`)
      setMaterials(data.materials);
      setCount(data.count)
      setStatistics({ total: data.total_count, today: data.today_count })
      setShowPagination(data.count > per)
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    if (loadingPage) {
      setLoadingPage(false)
    }
  }

  const getCourses = async () => {
    try {
      const courses = await api.get("/admin/courses");
      setCourses(courses.map(c => ({ ...c, label: c.name })).sort((a, b) => a.label.localeCompare(b.label)))
    } catch (e) {
      console.error(e)
    }
  }

  const getEditions = async () => {
    try {
      let editions = await api.get(`/admin/courses/${course.id}/editions`);
      editions = editions.filter(e => e.modules && e.modules.length > 0).sort((a, b) => { return new Date(b.locked_at) - new Date(a.locked_at) })
      setEditions(editions.map(e => ({ ...e, label: e.name })))
    } catch (e) {
      console.error(e)
    }
  }

  const onSearch = (searchText) => {
    setSearchQuery(searchText ?? "")
  }


  const onDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const save = () => {
    if (!module || (!currentMaterial && !files.length)) return
    setFormError("")
    if (currentMaterial) {
      edit()
    } else {
      add()
    }
  }

  const edit = async () => {
    setWaitingEdit(ButtonStatus.Loading)
    const newMaterial = {
      module_id: module.id,
    }
    if (lesson !== null) {
      newMaterial.lesson_id = lesson.id
    }
    try {
      await api.put(`/admin/materials/${currentMaterial.id}`, newMaterial);
      getMaterials()
      setOpenDialog(false)
    } catch (e) {
      console.error(e)
      setWaitingEdit(ButtonStatus.Error)
      setFormError("Impossibile salvare il materiale. Riprova.")
    }
  }

  const add = async () => {
    setWaitingAdd(true)
    const payload = {
      module_id: module.id,
      files,
    }

    if (lesson !== null) {
      payload.lesson_id = lesson.id
    }

    try {
      await api.post("/admin/materials", payload)
      if (page !== 0) {
        setPage(0)
      } else {
        await getMaterials()
      }

      setOpenDialog(false)
    } catch (e) {
      console.error(e)
      setFormError("Impossibile aggiungere i materiali. Riprova.")
    }
    setWaitingAdd(false)
  }


  const deleteMaterial = async (id) => {
    try {
      await api.delete(`/admin/materials/${id}`)
      getMaterials()
    } catch (e) {
      console.error(e)
    }
  }

  const onDialogClose = () => {
    setFormError("")
    setCourse(null)
    setEdition(null)
    setModule(null)
    setLesson(null)
    setFiles([])
    setDialogCourseSearch("")
    setDialogEditionSearch("")
    setDialogModuleSearch("")
    setDialogLessonSearch("")
    setUploaderReset((prev) => prev + 1)
    setCurrentMaterial(null)
    setWaitingEdit(false)
  }

  const visibleCourses = courses.filter((c) => ![CourseType.Webinar, CourseType.Bootcamp].includes(c?.type))

  const courseFilterOptions = [
    { id: -1, name: "Tutti i corsi" },
    ...visibleCourses,
  ]

  const editionFilterOptions = [
    { id: -1, name: "Tutte le edizioni" },
    ...[...filterEditions].sort((a, b) => a.name.localeCompare(b.name)),
  ]

  const moduleFilterOptions = [
    { id: -1, name: "Tutti i moduli" },
    ...filterEditions
      .filter((e) => e.id === filters.editionId)
      .flatMap((e) => e.modules ?? [])
      .sort((a, b) => a.name.localeCompare(b.name)),
  ]

  const lessonFilterOptions = [
    { id: -1, name: "Tutte le lezioni" },
    ...filterEditions
      .filter((e) => e.id === filters.editionId)
      .flatMap((e) => e.modules ?? [])
      .filter((m) => m.id === filters.moduleId)
      .flatMap((m) => m.lessons ?? [])
      .sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at)),
  ]

  const filteredCourseFilterOptions = filterSelectOptions(courseFilterOptions, filterCourseSearch)
  const filteredEditionFilterOptions = filterSelectOptions(editionFilterOptions, filterEditionSearch)
  const filteredModuleFilterOptions = filterSelectOptions(moduleFilterOptions, filterModuleSearch)
  const filteredLessonFilterOptions = filterSelectOptions(lessonFilterOptions, filterLessonSearch)

  const dialogCourseOptions = currentMaterial ? courses : visibleCourses
  const filteredDialogCourseOptions = filterSelectOptions(dialogCourseOptions, dialogCourseSearch)
  const filteredDialogEditionOptions = filterSelectOptions(editions, dialogEditionSearch)
  const filteredDialogModuleOptions = filterSelectOptions(modules, dialogModuleSearch)
  const filteredDialogLessonOptions = filterSelectOptions(
    [...lessons].sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at)),
    dialogLessonSearch
  )


  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Didattica - Materiali</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>

            <div className={styles.content}>
              <div className={styles.header}>
                <div><p className={styles.eyebrow}>Didattica</p><h1>Materiali</h1><p>Gestisci i file didattici associati a corsi, moduli e lezioni.</p></div>
                <CreateButton onClick={() => setOpenDialog(true)}>
                  Aggiungi materiali

                </CreateButton>
              </div>
              <Card appearance="modern" integratedTable title="Elenco materiali" actions={
                <div className={styles.counts} role="status">
                  <span className={styles.count}>{loading ? "Caricamento…" : `${count?.toLocaleString("it-IT") ?? 0} materiali`}</span>
                  <span className={styles.count} title="Materiali caricati oggi, indipendentemente dai filtri. Fuso orario italiano.">{statistics?.today?.toLocaleString("it-IT") ?? "—"} caricati oggi</span>
                </div>
              }>
              <div className={styles.search}>
                <TextInput appearance="modern"
                  onKeyUp={onSearch}
                  type="search"
                  placeholder={"Ricerca"}
                  style={{
                    backgroundColor: "var(--background-secondary-color)",
                  }} />
                <button type="button" className={styles.filterButton} aria-expanded={filterOpen} aria-controls="material-filters" onClick={() => setFilterOpen(!filterOpen)}><FilterIcon /> Filtri</button>
              </div>
              <div id="material-filters" className={styles.filters} hidden={!filterOpen}>
                <Select appearance="modern" portal
                  selected={courseFilterOptions.find((c) => c.id === (filters.courseId ?? -1))}
                  options={filteredCourseFilterOptions}
                  placeholder={"Corso"}
                  onSearch={(value) => setFilterCourseSearch(value ?? "")}
                  searchPlaceholder={"Cerca corso"}
                  onSelect={(item) => {
                    if (!item || item.id === -1) {
                      setFilters({ courseId: null, editionId: null, moduleId: null, lessonId: null })
                      return
                    }

                    setFilters({ courseId: item.id, editionId: null, moduleId: null, lessonId: null })
                  }}
                />

                <Select appearance="modern" portal
                  disabled={!filters.courseId}
                  selected={editionFilterOptions.find((e) => e.id === (filters.editionId ?? -1))}
                  options={filteredEditionFilterOptions}
                  placeholder={"Edizione"}
                  onSearch={(value) => setFilterEditionSearch(value ?? "")}
                  searchPlaceholder={"Cerca edizione"}
                  onSelect={(item) => {
                    if (!item || item.id === -1) {
                      setFilters((prev) => ({ ...prev, editionId: null, moduleId: null, lessonId: null }))
                      return
                    }

                    setFilters((prev) => ({ ...prev, editionId: item.id, moduleId: null, lessonId: null }))
                  }}
                />

                <Select appearance="modern" portal
                  disabled={!filters.editionId}
                  selected={moduleFilterOptions.find((m) => m.id === (filters.moduleId ?? -1))}
                  options={filteredModuleFilterOptions}
                  placeholder={"Modulo"}
                  onSearch={(value) => setFilterModuleSearch(value ?? "")}
                  searchPlaceholder={"Cerca modulo"}
                  onSelect={(item) => {
                    if (!item || item.id === -1) {
                      setFilters((prev) => ({ ...prev, moduleId: null, lessonId: null }))
                      return
                    }

                    setFilters((prev) => ({ ...prev, moduleId: item.id, lessonId: null }))
                  }}
                />

                <Select appearance="modern" portal
                  disabled={!filters.moduleId}
                  selected={lessonFilterOptions.find((l) => l.id === (filters.lessonId ?? -1))}
                  options={filteredLessonFilterOptions}
                  placeholder={"Lezione"}
                  onSearch={(value) => setFilterLessonSearch(value ?? "")}
                  searchPlaceholder={"Cerca lezione"}
                  onSelect={(item) => {
                    if (!item || item.id === -1) {
                      setFilters((prev) => ({ ...prev, lessonId: null }))
                      return
                    }

                    setFilters((prev) => ({ ...prev, lessonId: item.id }))
                  }}
                />
              </div>

              <div className={styles.materials}>
                {
                  loading === false && materials.length > 0 &&
                  <Table integrated
                    appearance="minimal"
                    columns={[
                      { key: "name", label: "NOME", type: "custom" },
                      { key: "file_type", label: "TIPO", type: "string" },
                      { key: "teacher_name", label: "DOCENTE", type: "string" },
                      { key: "course_name", label: "CORSO", type: "string", wrap: true },
                      { key: "edition_name", label: "EDIZIONE", type: "string", wrap: true },
                      { key: "module_name", label: "MODULO", type: "custom" },
                      { key: "lesson_name", label: "LEZIONE", type: "custom" },
                      { key: "created_at", label: "CARICATO IL", type: "datetime" },
                      { key: "_actions", label: "", type: "custom", handleClick: true, style: { width: 52 } },
                    ]}
                    elements={materials.map((material) => ({
                      ...material,
                      name: {
                        value: material.name ?? "-",
                        type: "string",
                        component: <div className={styles.name}>{material.name ?? "-"}</div>,
                      },
                      file_type: getMaterialFileType(material),
                      teacher_name: `${material.teacher?.name ?? ""} ${material.teacher?.surname ?? ""}`.trim(),
                      course_name: material.course?.name ?? "-",
                      edition_name: material.edition?.name ?? "-",
                      module_name: {
                        value: material.module?.name ?? "-",
                        type: "string",
                        component: <div className={styles.lessonName}>{material.module?.name ?? "-"}</div>,
                      },
                      lesson_name: {
                        value: material.lesson?.name ?? "-",
                        type: "string",
                        component: <div className={styles.lessonName}>{material.lesson?.name ?? "-"}</div>,
                      },
                      _actions: {
                        value: material.id,
                        type: "number",
                        component: (
                          <MeatBallsMenu
                            actions={[
                              { label: "Scarica", icon: DownloadIcon, onClick: () => onDownload(material.url) },
                              { label: "Elimina", icon: DeleteIcon, onClick: () => setMaterialToDelete(material.id), style: { color: "var(--sf-red)" } },
                              {
                                label: "Modifica",
                                icon: EditIcon,
                                onClick: () => {
                                  setWaitingEdit(false)
                                  setCurrentMaterial(material)
                                },
                                style: { color: "var(--sf-light-green)" },
                              },
                            ]}
                          />
                        ),
                      },
                    }))}
                  />
                }
                {
                  loading &&
                  <Table integrated
                    loading
                    loadingN={25}
                    appearance="minimal"
                    columns={[
                      { key: "name", label: "NOME", type: "string" },
                      { key: "file_type", label: "TIPO", type: "string" },
                      { key: "teacher_name", label: "DOCENTE", type: "string" },
                      { key: "course_name", label: "CORSO", type: "string" },
                      { key: "edition_name", label: "EDIZIONE", type: "string" },
                      { key: "module_name", label: "MODULO", type: "string" },
                      { key: "lesson_name", label: "LEZIONE", type: "string" },
                      { key: "created_at", label: "CARICATO IL", type: "datetime" },
                    ]}
                  />
                }

                {
                  loading === false && materials.length === 0 &&
                  <EmptyContent
                    Illustration={EmptyIllustration}
                    title={"Nessun materiale trovato"}
                    message={
                      <div style={{ display: "flex", alignItems: "center", }}>
                        <Button
                          appearance="subtle"
                          onClick={() => setOpenDialog(true)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: ".2rem"
                          }}
                        >
                          Aggiungi materiali
                          <AddIcon style={{width: '11px', height: '11px'}}/>
                        </Button>
                      </div>
                    }
                  />
                }
              </div>
              {
                showPagination &&
                <div className={styles.pagination}><Pagination total={count} per={per} page={page} group={width > 540 ? 5 : 0} onChange={p => setPage(p)} loading={loadingPage} /></div>
              }
              </Card>
            </div>
          </div>

          <Dialog appearance="modern"
            open={openDialog}
            title={currentMaterial ? "Modifica materiale" : t("materials.addFile")}
            style={{ width: "720px", maxWidth: "calc(100vw - 32px)" }}
            contentMaxHeight="78vh"
            onClose={() => { if (!waitingAdd && waitingEdit !== ButtonStatus.Loading) setOpenDialog(false) }}
            action={
              <Button
                appearance="subtle"
                onClick={save}
                disabled={!module || (!currentMaterial && !files.length) || waitingAdd || waitingEdit === ButtonStatus.Loading}
                status={currentMaterial ? waitingEdit : (waitingAdd ? ButtonStatus.Loading : null)}
              >
                {currentMaterial ? "Salva modifiche" : "Aggiungi materiali"}
              </Button>
            }

          >
            <div className={styles.dialog}>
              <p className={styles.formHint}>Seleziona corso, edizione e modulo. L’associazione a una lezione è facoltativa.</p>
              {currentMaterial && <div className={styles.materialPreview}><MaterialButton modern thumbnail material={currentMaterial} downloadable /></div>}
              {formError && <div className={styles.formError} role="alert">{formError}</div>}
              <div className={styles.formField}><span>Corso</span><Select appearance="modern" portal
                disabled={dialogCourseOptions.length === 0}
                placeholder={"Corso"}
                selected={dialogCourseOptions.find(c => c.id === course?.id) ?? null}
                options={filteredDialogCourseOptions}
                onSearch={(value) => setDialogCourseSearch(value ?? "")}
                searchPlaceholder={"Cerca corso"}
                onSelect={(item) => {
                  if (item?.id) {
                    setCourse(item)
                    setEdition(null)
                    setModule(null)
                    setLesson(null)
                    setDialogEditionSearch("")
                    setDialogModuleSearch("")
                    setDialogLessonSearch("")
                  }
                }} /></div>

              <div className={styles.formField}><span>Edizione</span><Select appearance="modern" portal
                disabled={editions.length === 0}
                placeholder={"Edizione"}
                selected={editions.find(e => e.id === edition?.id) ?? null}
                options={filteredDialogEditionOptions}
                onSearch={(value) => setDialogEditionSearch(value ?? "")}
                searchPlaceholder={"Cerca edizione"}
                onSelect={(item) => {
                  if (item?.id) {
                    setEdition(item)
                    setModule(null)
                    setLesson(null)
                    setDialogModuleSearch("")
                    setDialogLessonSearch("")
                  }
                }} /></div>

              <div className={styles.formField}><span>Modulo</span><Select appearance="modern" portal
                disabled={modules.length === 0}
                placeholder={"Modulo"}
                selected={modules.find(m => m.id === module?.id) ?? null}
                options={filteredDialogModuleOptions}
                onSearch={(value) => setDialogModuleSearch(value ?? "")}
                searchPlaceholder={"Cerca modulo"}
                onSelect={(item) => {
                  if (item?.id) {
                    setModule(item)
                    setLesson(null)
                    setDialogLessonSearch("")
                  }
                }} /></div>

              <div className={styles.formField}><span>Lezione</span><Select appearance="modern" portal
                disabled={lessons.length === 0}
                placeholder={"Lezione"}
                selected={lessons.find(l => l.id === lesson?.id) ?? null}
                options={filteredDialogLessonOptions}
                onSearch={(value) => setDialogLessonSearch(value ?? "")}
                searchPlaceholder={"Cerca lezione"}
                onSelect={(item) => {
                  if (item?.id) {
                    setLesson(item)
                  }
                }} /></div>
              {
                !currentMaterial &&
                <div className={styles.dropzoneContainer}>
                  <div className={styles.subtitle}>Materiali</div>
                  <Uploader
                    add_timestamp={true}
                    multiple={true}
                    reset={uploaderReset}
                    onProgress={(_, uploads) => {
                      setFiles(
                        (uploads || [])
                          .filter((upload) => Boolean(upload?.url))
                          .map((upload) => ({
                            url: upload.url,
                            name: upload?.file?.name || "material",
                            size: upload?.file?.size || 0,
                          }))
                      )
                    }}
                    onComplete={(uploads) => {
                      setFiles(
                        (uploads || [])
                          .filter((upload) => Boolean(upload?.url))
                          .map((upload) => ({
                            url: upload.url,
                            name: upload?.file?.name || "material",
                            size: upload?.file?.size || 0,
                          }))
                      )
                    }}
                    onDelete={(uploads) => {
                      setFiles(
                        (uploads || [])
                          .filter((upload) => Boolean(upload?.url))
                          .map((upload) => ({
                            url: upload.url,
                            name: upload?.file?.name || "material",
                            size: upload?.file?.size || 0,
                          }))
                      )
                    }}
                  />
                </div>
              }
            </div>
          </Dialog>
          <AlertDialog appearance="modern" open={materialToDelete} onClose={() => setMaterialToDelete(null)} title={"Elimina materiale?"} text={"Sei sicuro di voler eliminare questo materiale? Questa azione non può essere annullata."}
            actions={[
              {
                label: "Annulla",
                onClick: () => setMaterialToDelete(null)
              },
              {
                label: "Elimina",
                onClick: () => {
                  deleteMaterial(materialToDelete)
                  setMaterialToDelete(null)
                },
                accentColor: "var(--sf-red)",
              }
            ]} />
        </div>
      </div>

    </HeaderFooterLayout>
  )
}

export default Materials
